/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resetStateBooks } from "../../app/slices/bookSlice";
import { fetchUploads } from "../../app/slices/uploadSlice";
import { translate } from "../../app/utils";
import RelatedBooks from "../../components/relatedBooks/relatedBooks";
import { Box, Typography, Button, Divider, Chip, Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import moment from "moment";

const BookDetails = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [language] = useState(
    localStorage.getItem("currentLanguage")?.toUpperCase() || "EN",
  );

  const {
    get: { list: books },
  } = useSelector(({ uploads }) => uploads);

  const book = state?.book;

  useEffect(() => {
    dispatch(fetchUploads({ TYPE: "eBook", STATUS: "A" }));

    return () => {
      delete state?.event;
      dispatch(resetStateBooks(["get"]));
    };
  }, [book, dispatch, state]);

  if (!book) {
    return (
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h5" color="text.secondary">
          {translate("noBookSelected") === "noBookSelected" ? "No book selected" : translate("noBookSelected")}
        </Typography>
      </Box>
    );
  }

  const placeholderImg = "/placeholder-book.svg";

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/books")}
          sx={{ color: "text.secondary", textTransform: "none" }}
        >
          {translate("backToCollection") === "backToCollection" ? "Back to collection" : translate("backToCollection")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 6,
        }}
      >
        {/* Main Details Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Cover Image */}
          <Paper
            elevation={4}
            sx={{
              width: { xs: "100%", md: 320 },
              height: { xs: 400, md: 480 },
              flexShrink: 0,
              borderRadius: 3,
              overflow: "hidden",
              mx: { xs: "auto", md: 0 },
            }}
          >
            <Box
              component="img"
              src={book.COVER_IMAGE || placeholderImg}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = placeholderImg;
              }}
              alt={book[`TITLE_${language}`] || book.TITLE_EN}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Paper>

          {/* Details Content */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Chip
              label={book.CATEGORY || "eBook"}
              variant="outlined"
              size="small"
              sx={{ alignSelf: "flex-start", mb: 2, fontWeight: 600, color: "text.secondary" }}
            />

            <Typography
              variant="h3"
              fontWeight="800"
              sx={{ mb: 1, color: "text.primary", letterSpacing: -0.5 }}
            >
              {book[`TITLE_${language}`] || book.TITLE_EN}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body1"
                  sx={{ mr: 1, color: "text.secondary", fontWeight: 500 }}
                >
                  By
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  {book.AUTHOR_NAME || "Unknown Author"}
                </Typography>
              </Box>

              {(() => {
                if (!book.CREATED_AT || book.CREATED_AT === "null") return null;

                const isFormatValid = moment(
                  book.CREATED_AT,
                  "DD-MM-YYYY HH:mm:ss",
                  true,
                ).isValid();
                const isStandardValid = moment(book.CREATED_AT).isValid();

                if (!isFormatValid && !isStandardValid) return null;

                const dateStr = isFormatValid
                  ? moment(book.CREATED_AT, "DD-MM-YYYY HH:mm:ss").format(
                      "Do MMM YYYY",
                    )
                  : moment(book.CREATED_AT).format("Do MMM YYYY");

                return (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    Posted on:{" "}
                    <strong style={{ marginLeft: 4 }}>{dateStr}</strong>
                  </Typography>
                );
              })()}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1.5 }}>
              Summary
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                lineHeight: 1.8,
                fontSize: "1.05rem",
                whiteSpace: "pre-line",
              }}
            >
              {book[`SUMMARY_${language}`] ||
                book.SUMMARY_EN ||
                book[`DESCRIPTION_${language}`] ||
                book.DESCRIPTION_EN ||
                "No summary available for this book."}
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ mt: "auto", pt: 4, display: "flex", gap: 2 }}></Box>
          </Box>
        </Box>

        {/* Related Books Sidebar */}
        <Box sx={{ width: { xs: "100%", lg: 380 }, flexShrink: 0 }}>
          <Typography variant="h5" fontWeight="700" sx={{ mb: 3 }}>
            Related Books
          </Typography>
          <Box
            sx={{
              maxHeight: { xs: 400, lg: "calc(100vh - 200px)" },
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#bdbdbd",
                borderRadius: "4px",
              },
            }}
          >
            {books?.length > 0 && (
              <RelatedBooks
                orientation="column"
                books={books.filter(
                  (item) => item.UPLOAD_ID !== book.UPLOAD_ID,
                )}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BookDetails;
